import Image from 'next/image';
import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Button, RichInline } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { Prose } from '@/molecules';

import type { CSSProperties, FunctionComponent } from 'react';
import type { TypeImageCardProps } from './types';

/**
 * Tarjeta vertical (img + título + descripción + botón opcional). Soporta
 * máscara orgánica vía `imageShapeUrl` (relación `Forma` en Strapi).
 */
export const ImageCard: FunctionComponent<TypeImageCardProps> = ({
  className = '',
  image,
  imageShapeUrl,
  imagePosition,
  label,
  title,
  description,
  labelStyleClass,
  titleStyleClass,
  descriptionStyleClass,
  button,
  compact = false,
}: TypeImageCardProps) => {
  const maskStyle: CSSProperties | undefined = imageShapeUrl
    ? {
        WebkitMaskImage: `url(${imageShapeUrl})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: '100% 100%',
        maskImage: `url(${imageShapeUrl})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: '100% 100%',
      }
    : undefined;

  // Subrayado manuscrito que aparece al hacer hover en la tarjeta. Reusa el
  // mismo SVG y geometría (21px + translateY(50%)) que la regla global de
  // <mark>. `w-fit max-w-full` ciñe el trazo al ancho del texto sin desbordar
  // la columna cuando el título es largo.
  const hoverMarkCls = oneLine`relative w-fit max-w-full
    after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0
    after:h-[21px] after:translate-y-1/2 after:bg-[image:var(--mark-highlight-url,none)]
    after:bg-[length:100%_100%] after:bg-center after:bg-no-repeat after:opacity-0
    after:transition-opacity after:duration-300 after:content-['']
    group-hover/card:after:opacity-100`;

  return (
    <article
      className={`group/card flex h-full flex-col items-start ${
        compact ? 'gap-8' : 'gap-5'
      } ${className}`}
    >
      {image?.src && (
        <div
          className={`relative w-full overflow-hidden ${imageShapeUrl ? 'aspect-square' : 'aspect-[385/547]'}`}
          style={maskStyle}
        >
          <Image
            src={image.src}
            alt={image.alt ?? title ?? ''}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="group-hover/card:animate-brightness-pulse object-cover motion-reduce:group-hover/card:animate-none"
            style={
              imagePosition ? { objectPosition: imagePosition } : undefined
            }
          />
        </div>
      )}

      {(title || description) && (
        <div className="flex flex-col gap-2 px-2">
          {title && (
            <RichInline
              as="h3"
              html={title}
              className={oneLine`text-headline font-primary ${
                compact ? 'text-text-neutrals-900' : 'text-text-primary'
              } ${
                titleStyleClass ??
                (compact
                  ? 'text-[24px] leading-[32px] font-bold'
                  : 'text-headline-h3 font-bold')
              } ${hoverMarkCls}`}
            />
          )}
          {description && (
            <Prose
              className={oneLine`text-body ${
                descriptionStyleClass ??
                (compact
                  ? 'text-text-neutrals-900 [&_p]:text-text-neutrals-900 text-[16px] leading-[24px] [&_p]:text-[16px] [&_p]:leading-[24px]'
                  : '[&_p]:text-body-l')
              }`}
              html={description}
            />
          )}
        </div>
      )}

      {(button || label) && (
        // Mobile: `flex-col-reverse` apila el `label` ARRIBA del botón
        // (Figma "¡Top!" sobre el CTA). sm+ vuelve al row horizontal con
        // label a la derecha (desktop ya estaba OK), sin tocar el orden
        // del JSX.
        <div className="mt-auto flex flex-col-reverse items-start gap-3 px-2 sm:flex-row sm:items-center">
          {button && button.onClick && (
            <Button
              size={button.size ?? ButtonSize.SMALL}
              variant={button.variant ?? ButtonVariant.PRIMARY}
              type="button"
              onClick={button.onClick}
              className="group-hover/card:text-neutrals-0! group-hover/card:border-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]! group-hover/card:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]!"
            >
              {button.label}
            </Button>
          )}
          {button && !button.onClick && (
            <NextLink
              href={button.href}
              prefetch={false}
              passHref
              legacyBehavior
            >
              <Button
                size={button.size ?? ButtonSize.SMALL}
                variant={button.variant ?? ButtonVariant.PRIMARY}
                as="a"
                className="group-hover/card:text-neutrals-0! group-hover/card:border-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]! group-hover/card:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]!"
              >
                {button.label}
              </Button>
            </NextLink>
          )}
          {label && (
            <RichInline
              html={label}
              className={oneLine`font-accent text-primary-500 leading-none ${
                labelStyleClass ?? 'text-5xl'
              }`}
            />
          )}
        </div>
      )}
    </article>
  );
};

ImageCard.displayName = 'ImageCard';
